import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  DocumentTextIcon, 
  TrashIcon, 
  ArrowDownTrayIcon,
  XMarkIcon,
  PaperClipIcon,
  CalendarDaysIcon,
  DocumentIcon,
  EyeIcon
} from '@heroicons/react/24/outline';

interface Document {
  id?: number;
  instruction_id: number;
  document_type: string;
  description: string;
  file_name: string;
  file_path: string;
  file_size: number;
  mime_type: string;
  created_at?: string;
}

interface ClientInstructionDocumentsProps {
  instructionId: string;
  clientId: string;
  onDocumentChange?: () => void; // ✅ ADD THIS
}

const API_URL = process.env.REACT_APP_API_URL || '';

const ClientInstructionDocuments: React.FC<ClientInstructionDocumentsProps> = ({ 
  instructionId, 
  clientId,
  onDocumentChange  // ✅ ADD THIS
}) => {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [description, setDescription] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isDeleting, setIsDeleting] = useState<number | null>(null);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [previewDocument, setPreviewDocument] = useState<Document | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);

  useEffect(() => {
    if (instructionId) {
      fetchDocuments();
    }
  }, [instructionId]);

  const fetchDocuments = async () => {
  if (!instructionId) {
    console.log('📄 No instructionId provided, skipping fetch');
    return;
  }
  
  console.log(`📄 Fetching documents for instruction ID: ${instructionId}`);
  setLoadingDocuments(true);
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      console.error('❌ No authentication token found');
      throw new Error('No authentication token found');
    }

    const url = `${API_URL}/api/instruction-documents?instructionId=${instructionId}`;
    console.log(`📄 Fetching from: ${url}`);

    const response = await axios.get(
      url,
      {
        headers: { Authorization: `Bearer ${token}` }
      }
    );
    
    console.log('📄 Response status:', response.status);
    console.log('📄 Response data:', response.data);
    
    let documentsData = [];
    if (response.data && Array.isArray(response.data)) {
      documentsData = response.data;
    } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
      documentsData = response.data.data;
    } else {
      documentsData = [];
    }
    
    console.log(`📄 Found ${documentsData.length} documents`);
    if (documentsData.length > 0) {
      console.log('📄 Documents:', documentsData);
    }
    
    setDocuments(documentsData);
    
  } catch (error: any) {
    console.error('❌ Error fetching documents:', error);
    console.error('❌ Error response:', error.response?.data);
    setError('Failed to load documents');
  } finally {
    setLoadingDocuments(false);
  }
};

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setSelectedFile(file);
    
    if (file && !description) {
      setDescription(file.name.split('.')[0].replace(/[_-]/g, ' '));
    }
  };

  const handleUpload = async (e: React.FormEvent) => {
  e.preventDefault();
  
  if (!selectedFile) {
    setError('Please select a file to upload');
    return;
  }

  console.log('📤 Starting document upload...');
  console.log('📤 Selected file:', selectedFile.name, selectedFile.size, 'bytes');
  console.log('📤 instructionId:', instructionId);
  console.log('📤 clientId:', clientId);
  console.log('📤 description:', description || selectedFile.name);

  setIsLoading(true);
  setError('');
  setSuccess('');
  setUploadProgress(0);

  try {
    const token = localStorage.getItem('token');
    if (!token) {
      console.error('❌ No authentication token found');
      throw new Error('No authentication token found');
    }

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('instructionId', instructionId);
    formData.append('clientId', clientId);
    formData.append('description', description || selectedFile.name);

    console.log('📤 Sending upload request to:', `${API_URL}/api/instruction-documents/upload`);

    const response = await axios.post(
      `${API_URL}/api/instruction-documents/upload`,
      formData,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const progress = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress(progress);
          }
        },
      }
    );

        console.log('✅ Upload response:', response.data);
    console.log('✅ Upload successful!');

    setSuccess('Document uploaded successfully!');
    setSelectedFile(null);
    setDescription('');
    setUploadProgress(0);
    
    // ✅ Refresh documents list first
    await fetchDocuments();
    
    // ✅ Then refresh the notes tab
    console.log('📝 Calling onDocumentChange from upload');
    if (onDocumentChange) {
      onDocumentChange();
    }
    
  } catch (error: any) {
    console.error('❌ Error uploading document:', error);
    console.error('❌ Error response:', error.response?.data);
    setError(error.response?.data?.message || 'Failed to upload document');
  } finally {
    setIsLoading(false);
  }
};

  const handleDownload = async (doc: Document) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      const response = await axios.get(
        `${API_URL}/api/instruction-documents/download/${doc.id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.file_name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
    } catch (error: any) {
      console.error('Error downloading document:', error);
      setError('Failed to download document');
    }
  };

  const handleViewFile = async (doc: Document) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      setIsPreviewLoading(true);
      setPreviewDocument(doc);

      const response = await axios.get(
        `${API_URL}/api/instruction-documents/download/${doc.id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      const url = URL.createObjectURL(response.data);
      setPreviewUrl(url);

    } catch (error: any) {
      console.error('Error previewing document:', error);
      setError('Failed to load document preview');
      setPreviewDocument(null);
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const closePreviewModal = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewDocument(null);
    setPreviewUrl('');
  };

  const handleDelete = async (documentId: number) => {
  console.log(`🗑️ Deleting document ${documentId}...`);
  if (!window.confirm('Are you sure you want to delete this document?')) {
    console.log('🗑️ Delete cancelled by user');
    return;
  }

  setIsDeleting(documentId);
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      console.error('❌ No authentication token found');
      throw new Error('No authentication token found');
    }

    console.log(`🗑️ Sending DELETE request to: ${API_URL}/api/instruction-documents/${documentId}`);
    const response = await axios.delete(
      `${API_URL}/api/instruction-documents/${documentId}`,
      {
        headers: { Authorization: `Bearer ${token}` }
      }
    );
    
        console.log('🗑️ Delete response:', response.data);

    setSuccess('Document deleted successfully!');
    
    // ✅ Refresh documents list first
    await fetchDocuments();
    
    // ✅ Then refresh the notes tab
    console.log('📝 Calling onDocumentChange from delete');
    if (onDocumentChange) {
      onDocumentChange();
    }
    
  } catch (error: any) {
    console.error('❌ Error deleting document:', error);
    console.error('❌ Error response:', error.response?.data);
    setError('Failed to delete document');
  } finally {
    setIsDeleting(null);
  }
};

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDocumentIcon = (mimeType: string) => {
    if (mimeType.includes('pdf')) return '📄';
    if (mimeType.includes('image')) return '🖼️';
    if (mimeType.includes('word') || mimeType.includes('document')) return '📝';
    if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return '📊';
    return '📎';
  };

  return (
    <div className="space-y-6">
      <h4 className="text-lg font-semibold text-gray-800">Instruction Documents</h4>
      <p className="text-sm text-gray-500">Manage documents for this instruction</p>

      {/* Upload Form */}
      <div className="bg-gradient-to-br from-white to-blue-50 p-6 rounded-xl shadow-lg border border-blue-100">
        <div className="flex items-center mb-6">
          <div className="bg-blue-100 p-2 rounded-lg mr-3">
            <DocumentIcon className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h4 className="text-lg font-semibold text-gray-800">Upload New Document</h4>
            <p className="text-sm text-gray-500">Add files to this instruction's document repository</p>
          </div>
        </div>
        
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center">
              <XMarkIcon className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <p className="text-sm font-medium text-red-800">{error}</p>
              </div>
            </div>
          </div>
        )}
        
        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center">
              <DocumentTextIcon className="h-5 w-5 text-green-400" />
              <div className="ml-3">
                <p className="text-sm font-medium text-green-800">{success}</p>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleUpload} className="space-y-6">
          <div className="grid grid-cols-1 gap-6">
            {/* Description */}
            <div className="space-y-2">
              <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                Description
              </label>
              <input
                type="text"
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter document description"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white transition-colors"
              />
            </div>
          </div>

          {/* File Input */}
          <div className="space-y-2">
            <label htmlFor="file" className="block text-sm font-medium text-gray-700">
              Select File <span className="text-red-500">*</span>
            </label>
            <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg hover:border-blue-400 transition-colors">
              <div className="space-y-2 text-center">
                <PaperClipIcon className="mx-auto h-12 w-12 text-gray-400" />
                <div className="flex text-sm text-gray-600">
                  <label
                    htmlFor="file"
                    className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500"
                  >
                    <span>Upload a file</span>
                    <input
                      id="file"
                      type="file"
                      onChange={handleFileSelect}
                      className="sr-only"
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.txt,text/plain"
                      required
                    />
                  </label>
                  <p className="pl-1">or drag and drop</p>
                </div>
                <p className="text-xs text-gray-500">
                  PDF, JPG, PNG, DOC, DOCX, TXT up to 10MB
                </p>
                {selectedFile && (
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
                    <div className="flex items-center">
                      <DocumentTextIcon className="w-5 h-5 text-blue-500 mr-2" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-800 truncate">
                          {selectedFile.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatFileSize(selectedFile.size)}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Upload Progress */}
          {isLoading && uploadProgress > 0 && (
            <div className="space-y-3">
              <div className="flex justify-between text-sm text-gray-600">
                <span>Uploading...</span>
                <span className="font-medium">{uploadProgress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-gradient-to-r from-blue-500 to-blue-600 h-2 rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
            </div>
          )}

          {/* Upload and Cancel Buttons */}
          <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4 pt-2">
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 px-4 rounded-lg hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center shadow-md hover:shadow-lg"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Uploading...
                </>
              ) : (
                <>
                  <DocumentTextIcon className="w-5 h-5 mr-2" />
                  Upload Document
                </>
              )}
            </button>
            
            <button
              type="button"
              onClick={() => {
                setSelectedFile(null);
                setDescription('');
                setUploadProgress(0);
                setError('');
                setSuccess('');
              }}
              disabled={isLoading}
              className="flex-1 bg-gradient-to-r from-gray-500 to-gray-600 text-white py-3 px-4 rounded-lg hover:from-gray-600 hover:to-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center shadow-md hover:shadow-lg"
            >
              <XMarkIcon className="w-5 h-5 mr-2" />
              Cancel
            </button>
          </div>
        </form>
      </div>

      {/* Documents List */}
      <div className="bg-gradient-to-br from-white to-gray-50 p-6 rounded-xl shadow-lg border border-gray-200">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h4 className="text-lg font-semibold text-gray-800">Uploaded Documents</h4>
            <p className="text-sm text-gray-500 mt-1">
              {documents.length} document{documents.length !== 1 ? 's' : ''} uploaded
            </p>
          </div>
          <button
            onClick={fetchDocuments}
            className="flex items-center text-sm font-medium text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 px-3 py-1 rounded-lg transition-colors"
            disabled={loadingDocuments}
          >
            {loadingDocuments ? (
              <svg className="animate-spin w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : (
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            )}
            Refresh
          </button>
        </div>

        {loadingDocuments ? (
          <div className="text-center py-12">
            <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <svg className="animate-spin w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
            <p className="text-gray-600">Loading documents...</p>
          </div>
        ) : documents.length === 0 ? (
          <div className="text-center py-12">
            <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <DocumentTextIcon className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No documents uploaded yet</h3>
            <p className="text-gray-500 max-w-md mx-auto">
              Upload your first document using the form above.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {documents.map((doc) => (
              <div
                key={doc.id}
                className="bg-white rounded-xl border border-gray-200 hover:border-blue-300 hover:shadow-lg transition-all duration-200 overflow-hidden group"
              >
                <div className="p-5">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center mr-3">
                        <span className="text-xl">{getDocumentIcon(doc.mime_type)}</span>
                      </div>
                    </div>
                    <div className="text-xs text-gray-500 flex items-center">
                      <CalendarDaysIcon className="w-3 h-3 mr-1" />
                      {doc.created_at ? formatDate(doc.created_at) : 'N/A'}
                    </div>
                  </div>

                  <div className="mb-4">
                    <h5 className="font-medium text-gray-900 mb-1 truncate">
                      {doc.description || doc.file_name}
                    </h5>
                    <div className="flex items-center justify-between text-sm text-gray-500">
                      <div className="flex items-center flex-1 min-w-0">
                        <PaperClipIcon className="w-4 h-4 mr-1 flex-shrink-0" />
                        <span className="truncate">{doc.file_name}</span>
                      </div>
                      <button
                        onClick={() => handleViewFile(doc)}
                        className="ml-2 text-blue-600 hover:text-blue-800 text-sm font-medium hover:underline flex-shrink-0"
                      >
                        View File
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                    <div className="flex items-center">
                      <span className="bg-gray-100 px-2 py-1 rounded text-xs">
                        {doc.mime_type.split('/')[1]?.toUpperCase() || 'FILE'}
                      </span>
                      <span className="mx-2">•</span>
                      <span>{formatFileSize(doc.file_size)}</span>
                    </div>
                  </div>

                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleDownload(doc)}
                      className="flex-1 bg-blue-50 text-blue-700 py-2 px-3 rounded-lg hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-300 focus:ring-offset-1 transition-colors flex items-center justify-center text-sm font-medium"
                    >
                      <ArrowDownTrayIcon className="w-4 h-4 mr-2" />
                      Download
                    </button>
                    <button
                      onClick={() => doc.id && handleDelete(doc.id)}
                      disabled={isDeleting === doc.id}
                      className="flex-1 bg-red-50 text-red-600 py-2 px-3 rounded-lg hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-300 focus:ring-offset-1 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-sm font-medium"
                    >
                      {isDeleting === doc.id ? (
                        <>
                          <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Deleting...
                        </>
                      ) : (
                        <>
                          <TrashIcon className="w-4 h-4 mr-2" />
                          Delete
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Preview Modal */}
      {previewDocument && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div 
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
              aria-hidden="true"
              onClick={closePreviewModal}
            ></div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <h3 className="text-lg font-medium leading-6 text-gray-900" id="modal-title">
                      {previewDocument.description || previewDocument.file_name}
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      {previewDocument.file_name} • {formatFileSize(previewDocument.file_size)}
                    </p>
                  </div>
                  <button
                    onClick={closePreviewModal}
                    className="text-gray-400 hover:text-gray-500 focus:outline-none"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="mt-2">
                  {isPreviewLoading ? (
                    <div className="flex flex-col items-center justify-center py-16">
                      <svg className="animate-spin h-12 w-12 text-blue-500 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <p className="text-gray-600">Loading preview...</p>
                    </div>
                  ) : previewUrl ? (
                    <div className="border rounded-lg overflow-hidden bg-gray-50">
                      {previewDocument.mime_type.startsWith('image/') ? (
                        <img 
                          src={previewUrl} 
                          alt={previewDocument.file_name}
                          className="max-w-full h-auto mx-auto"
                        />
                      ) : previewDocument.mime_type === 'application/pdf' ? (
                        <iframe
                          src={previewUrl}
                          title={previewDocument.file_name}
                          className="w-full h-[70vh]"
                        />
                      ) : (
                        <div className="text-center py-16 px-4">
                          <DocumentTextIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                          <p className="text-gray-600 mb-4">
                            Preview not available for this file type.
                          </p>
                          <button
                            onClick={() => handleDownload(previewDocument)}
                            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                          >
                            <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
                            Download to view
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-16">
                      <p className="text-red-600">Failed to load document preview.</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={() => previewDocument && handleDownload(previewDocument)}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
                  Download
                </button>
                <button
                  type="button"
                  onClick={closePreviewModal}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientInstructionDocuments;